import { ExtraSelection, PartyData, PartyExtra } from '@types';
import { AlertModal, Card, EmptyState, LoadingSpinner } from '_atoms';
import PartyService from '_services/PartyService';
import React, { useCallback, useEffect, useState } from 'react';
import { MdAdd, MdArrowBack, MdRemove, MdShoppingCart } from 'react-icons/md';

interface ChoosePartyExtrasProps {
  selectedExtras: ExtraSelection[];
  onNext: (data?: Partial<PartyData>) => void;
  onPrevious?: () => void;
  onStepComplete: (data: Partial<PartyData>) => void;
}

export const ChoosePartyExtras: React.FC<ChoosePartyExtrasProps> = ({
  selectedExtras,
  onNext,
  onPrevious,
  onStepComplete,
}) => {
  const [partyExtras, setPartyExtras] = useState<PartyExtra[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentSelectedExtras, setCurrentSelectedExtras] = useState<
    ExtraSelection[]
  >(selectedExtras || []);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    type: 'info' | 'error' | 'success' | 'warning';
    title: string;
    message: string;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  });

  const showAlert = useCallback(
    (
      type: 'info' | 'error' | 'success' | 'warning',
      title: string,
      message: string,
    ) => {
      setAlertModal({ isOpen: true, type, title, message });
    },
    [],
  );

  const loadPartyExtras = useCallback(async () => {
    setIsLoading(true);
    try {
      const responseData = await PartyService.getPartyExtras();
      setPartyExtras((responseData as unknown as PartyExtra[]) || []);
    } catch (error) {
      console.error('Error loading party extras:', error);
      showAlert(
        'error',
        'Error',
        'Failed to load party extras. Please try again.',
      );
    } finally {
      setIsLoading(false);
    }
  }, [showAlert]);

  useEffect(() => {
    loadPartyExtras();
  }, [loadPartyExtras]);

  useEffect(() => {
    // Validate extras whenever they change
    const error = validateExtras(currentSelectedExtras);
    setErrorMessage(error);
  }, [currentSelectedExtras]);

  const closeAlert = () => {
    setAlertModal({ isOpen: false, type: 'info', title: '', message: '' });
  };

  const validateExtras = (extras: ExtraSelection[]): string => {
    for (const selection of extras) {
      if (selection.extra.type === 'walker' && selection.quantity > 5) {
        return 'Walker extras are limited to a maximum of 5.';
      }
    }
    return '';
  };

  const handleExtraQuantityChange = (extra: PartyExtra, change: number) => {
    const existingSelection = currentSelectedExtras.find(
      e => e.extra.id === extra.id,
    );
    const currentQuantity = existingSelection?.quantity || 0;
    const newQuantity = currentQuantity + change;

    // Check if we're going below 0
    if (newQuantity < 0) return;

    // Limit walker type extras to maximum of 5
    if (extra.type === 'walker' && newQuantity > 5) {
      return;
    }

    let updatedExtras: ExtraSelection[];

    if (newQuantity === 0) {
      // Remove extra from selection
      updatedExtras = currentSelectedExtras.filter(
        e => e.extra.id !== extra.id,
      );
    } else if (existingSelection) {
      // Update existing selection
      updatedExtras = currentSelectedExtras.map(e =>
        e.extra.id === extra.id ? { ...e, quantity: newQuantity } : e,
      );
    } else {
      // Add new selection
      updatedExtras = [
        ...currentSelectedExtras,
        { extra, quantity: newQuantity },
      ];
    }

    setCurrentSelectedExtras(updatedExtras);
    onStepComplete({ selectedExtras: updatedExtras });
  };

  const handleContinue = () => {
    onNext({ selectedExtras: currentSelectedExtras });
  };

  const calculateTotal = () => {
    return currentSelectedExtras.reduce(
      (total, extraSelection) =>
        total + extraSelection.extra.price * extraSelection.quantity,
      0,
    );
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold text-gray-900">Choose Extras</h2>
        <p className="text-gray-600 text-lg">
          Add some extra fun to your party (optional)
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : partyExtras.length === 0 ? (
        <EmptyState
          title="No Party Extras Available"
          description="There are currently no party extras available for booking."
          icon={MdShoppingCart}
        />
      ) : (
        <div className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {partyExtras.map(extra => {
              const currentSelection = currentSelectedExtras.find(
                e => e.extra.id === extra.id,
              );
              const currentQuantity = currentSelection?.quantity || 0;

              return (
                <div key={extra.id}>
                  <Card className="w-full h-full transition-all duration-200 hover:shadow-md">
                    <div className="p-0">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                              {extra.name}
                            </h3>
                          </div>
                        </div>
                      </div>

                      <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                        {extra.description}
                      </p>

                      {extra.photoUrl && (
                        <div className="mb-4">
                          <img
                            src={extra.photoUrl}
                            alt={extra.name}
                            className="w-full h-32 object-contain rounded-lg"
                            onError={e => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        </div>
                      )}

                      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                        <span className="text-xl font-bold text-green-600">
                          ${extra.price}
                        </span>

                        {/* Quantity Controls */}
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleExtraQuantityChange(extra, -1)}
                            disabled={currentQuantity === 0}
                            className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                          >
                            <MdRemove className="w-4 h-4 text-gray-600" />
                          </button>

                          <span className="w-8 text-center font-medium text-gray-900">
                            {currentQuantity}
                          </span>

                          <button
                            onClick={() => handleExtraQuantityChange(extra, 1)}
                            className="w-8 h-8 rounded-full bg-blue-100 hover:bg-blue-200 flex items-center justify-center transition-colors"
                          >
                            <MdAdd className="w-4 h-4 text-blue-600" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </Card>
                </div>
              );
            })}
          </div>

          {/* Selected Extras Summary */}
          {currentSelectedExtras.length > 0 && (
            <Card className="bg-gray-50 border-gray-200">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Selected Extras (
                  {currentSelectedExtras.reduce(
                    (sum, selection) => sum + selection.quantity,
                    0,
                  )}{' '}
                  items)
                </h3>
                <div className="space-y-2">
                  {currentSelectedExtras.map(extraSelection => (
                    <div
                      key={extraSelection.extra.id}
                      className="flex items-center justify-between"
                    >
                      <span className="text-gray-700">
                        {extraSelection.extra.name} (x{extraSelection.quantity})
                      </span>
                      <span className="font-medium text-gray-900">
                        $
                        {(
                          extraSelection.extra.price * extraSelection.quantity
                        ).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between pt-4 mt-4 border-t border-gray-300">
                  <span className="text-lg font-semibold text-gray-900">
                    Total Extras:
                  </span>
                  <span className="text-xl font-bold text-green-600">
                    ${calculateTotal().toFixed(2)}
                  </span>
                </div>
              </div>
            </Card>
          )}

          {/* Error Message */}
          {errorMessage && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 text-sm font-medium">{errorMessage}</p>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-6">
            {onPrevious && (
              <button
                onClick={onPrevious}
                className="inline-flex items-center px-4 py-3 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
              >
                <MdArrowBack className="mr-2" />
                Back
              </button>
            )}
            <div className="flex-1" />
            <button
              onClick={handleContinue}
              disabled={!!errorMessage}
              className="inline-flex items-center px-6 sm:px-8 py-4 sm:py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md text-base sm:text-sm"
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={closeAlert}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
      />
    </div>
  );
};
